import type {
  AgentRuntimeActionOutcome,
  AgentRuntimeActiveEditOperation,
  AgentType,
} from "@cord/shared"
import { NEW_SYNC_WEBSOCKET_PAGE_ROW_CAP } from "@cord/shared"
import { patchActiveRuntimeSnapshot } from "@/lib/thread-sync-active-runtime"
import {
  evaluateThreadSyncReplayWorkerEligibility,
  type ThreadSyncReplayWorkerBypassReason,
} from "@/lib/thread-sync-replay-worker-safety"
import {
  capturePartialAuthoritativeRuntimeState,
  finalizeAuthoritativeRuntimeState,
  handleRuntimeApplyFailure,
  prepareRuntimeForAuthoritativeApply,
  restoreRuntimeAfterFailedApply,
  type ThreadSyncRuntimeState,
} from "@/lib/thread-sync-runtime-cache"
import { isNonAuthoritativeRuntimeMaterialization } from "@/lib/thread-sync-runtime-materialization"
import type { ThreadSyncResumeState } from "@/lib/thread-sync-resume-state"
import {
  type ComputedRawSyncEventsBatch,
  type AgentRuntimeComputationState,
  computeRawSyncEventsBatch,
} from "@/lib/agent-runtime-sync-batch-computation"
import {
  applyThreadSyncBaselineEventsToManagedRuntimeState,
} from "@/lib/thread-sync-inactive-replica-computation"
import {
  computeRawSyncEventsBatchOffMainThread,
  disposeThreadSyncReplayWorkerSession,
} from "@/lib/thread-sync-replay-worker-client"
import type { ThreadSyncReplayWorkerEvent } from "@/lib/thread-sync-replay-worker-types"
import { debugInfo, debugWarn } from "@/stores/debug-logs"
import { useAgentRuntime } from "@/stores/agent-runtime"

export type ThreadSyncRuntimeApplyResult = {
  status: "applied" | "noop" | "error"
  maxEventId: number | null
}

export type ThreadSyncChunkedRuntimeApplyResult = ThreadSyncRuntimeApplyResult & {
  completedAllChunks: boolean
}

/**
 * Reuse roughly one websocket-sized page per apply slice so large cold-history
 * batches can yield between chunks without exploding the number of tiny replay
 * transactions. If chunked replay still blocks the UI, tune this alongside the
 * transport page geometry rather than letting the browser path drift.
 */
export const THREAD_SYNC_BULK_APPLY_CHUNK_EVENT_COUNT = NEW_SYNC_WEBSOCKET_PAGE_ROW_CAP

/**
 * Yield with a zero-delay timer so the browser can process paint and input work
 * between bulk replay slices while preserving event order. If this proves too
 * eager or too sparse in practice, revisit the scheduler deliberately instead of
 * hardcoding ad hoc microtask loops at call sites.
 */
export const THREAD_SYNC_BULK_APPLY_YIELD_DELAY_MS = 0

/**
 * Chunk replay should normally stay comfortably below a human-noticeable long
 * task. Log only when a batch computation crosses this threshold so we learn
 * about pathological sessions without writing a debug entry for every ordinary
 * chunk.
 */
export const THREAD_SYNC_REPLAY_COMPUTATION_SLOW_LOG_THRESHOLD_MS = 200

type RuntimeApplyPreparedState = ReturnType<typeof prepareRuntimeForAuthoritativeApply>

type PerformStoreApplyResult = {
  status: "applied" | "noop" | "error"
  maxEventId: number | null
}

function finalizeSuccessfulRuntimeApply(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
  connectionVersion: number
  resume: Pick<ThreadSyncResumeState, "cursor" | "historyToken">
  persistAsPartial: boolean
  nowMs: number
}): void {
  args.runtime.optimisticSeed = null
  if (args.persistAsPartial) {
    capturePartialAuthoritativeRuntimeState({
      activeThreadId: args.activeThreadId,
      sessionId: args.sessionId,
      runtime: args.runtime,
      connectionVersion: args.connectionVersion,
      resume: args.resume,
      nowMs: args.nowMs,
    })
    return
  }

  finalizeAuthoritativeRuntimeState({
    activeThreadId: args.activeThreadId,
    sessionId: args.sessionId,
    runtime: args.runtime,
    connectionVersion: args.connectionVersion,
    resume: args.resume,
    nowMs: args.nowMs,
  })
}

function runThreadSyncRuntimeApplyTransaction(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
  connectionVersion: number
  resumeForPersistence: Pick<ThreadSyncResumeState, "cursor" | "historyToken">
  sourceCursor: number
  persistAsPartial: boolean
  nowMs: number
  failureReason: string
  onRecoverableError?: () => void
  performStoreApply: (preparedState: RuntimeApplyPreparedState) => PerformStoreApplyResult
}): ThreadSyncRuntimeApplyResult {
  if (args.activeThreadId !== args.sessionId) {
    return { status: "noop", maxEventId: null }
  }

  const patchedOverlay = capturePatchedSnapshotOverlay()

  const preparedRuntimeState: RuntimeApplyPreparedState = prepareRuntimeForAuthoritativeApply({
    activeThreadId: args.activeThreadId,
    sessionId: args.sessionId,
    runtime: args.runtime,
  })

  try {
    const result = args.performStoreApply(preparedRuntimeState)
    const hasCursorOnlyProgress =
      result.status === "noop" &&
      result.maxEventId !== null &&
      result.maxEventId > args.sourceCursor
    if (result.status === "error") {
      handleRuntimeApplyFailure({
        activeThreadId: args.activeThreadId,
        sessionId: args.sessionId,
        runtime: args.runtime,
        preparedState: preparedRuntimeState,
        reason: args.failureReason,
      })
      args.onRecoverableError?.()
      return { status: "error", maxEventId: result.maxEventId }
    }

    if (result.status === "noop" && !hasCursorOnlyProgress) {
      restoreRuntimeAfterFailedApply({
        activeThreadId: args.activeThreadId,
        sessionId: args.sessionId,
        runtime: args.runtime,
        preparedState: preparedRuntimeState,
      })
      return { status: "noop", maxEventId: result.maxEventId }
    }

    finalizeSuccessfulRuntimeApply({
      activeThreadId: args.activeThreadId,
      sessionId: args.sessionId,
      runtime: args.runtime,
      connectionVersion: args.connectionVersion,
      resume: args.resumeForPersistence,
      persistAsPartial: args.persistAsPartial,
      nowMs: args.nowMs,
    })
    reapplyPatchedSnapshotOverlay(patchedOverlay)
    args.runtime.lastApplyError = null
    return {
      status: hasCursorOnlyProgress ? "applied" : result.status,
      maxEventId: result.maxEventId,
    }
  } catch (error) {
    handleRuntimeApplyFailure({
      activeThreadId: args.activeThreadId,
      sessionId: args.sessionId,
      runtime: args.runtime,
      preparedState: preparedRuntimeState,
      reason: error instanceof Error ? error.message : args.failureReason,
    })
    args.onRecoverableError?.()
    return { status: "error", maxEventId: null }
  }
}

function yieldAfterBulkApplySlice(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, THREAD_SYNC_BULK_APPLY_YIELD_DELAY_MS)
  })
}

const COMPUTED_RUNTIME_STATE_VERSION_CONFLICT = Symbol("computed_runtime_state_version_conflict")
/**
 * Replay-worker bypass logging is only there to surface distinct large-runtime
 * conditions during one tab session. Keeping a bounded LRU avoids turning the
 * dedupe index itself into unbounded per-session memory while still retaining
 * enough recent sessions to suppress repeated log spam during normal switching.
 */
const REPLAY_WORKER_BYPASS_LOG_SIGNATURE_LRU_MAX_SESSIONS = 128
const replayWorkerBypassLogSignatureBySessionId = new Map<string, string>()

function buildComputationStateFromActiveStore(): {
  currentState: AgentRuntimeComputationState
  stateVersion: number
} {
  const store = useAgentRuntime.getState()
  return {
    currentState: {
      agentType: store.agentType,
      snapshot: store.snapshot,
      lastAppliedTimestamp: store.lastAppliedTimestamp,
      lastEventId: store.lastEventId,
      rawOpenCodeProjection: store.rawOpenCodeProjection,
      rawClaudeProjection: store.rawClaudeProjection,
      rawCursorProjection: store.rawCursorProjection,
    },
    stateVersion: store.stateVersion,
  }
}

function nowForReplayComputationMeasurement(): number {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now()
  }

  return Date.now()
}

function maybeLogReplayWorkerBypass(args: {
  sessionId: string
  reason: string
  details: { [key: string]: unknown }
}): void {
  const signature = JSON.stringify({ reason: args.reason, ...args.details })
  if (replayWorkerBypassLogSignatureBySessionId.get(args.sessionId) === signature) {
    return
  }

  replayWorkerBypassLogSignatureBySessionId.delete(args.sessionId)
  replayWorkerBypassLogSignatureBySessionId.set(args.sessionId, signature)
  while (
    replayWorkerBypassLogSignatureBySessionId.size >
    REPLAY_WORKER_BYPASS_LOG_SIGNATURE_LRU_MAX_SESSIONS
  ) {
    const oldestSessionId = replayWorkerBypassLogSignatureBySessionId.keys().next().value
    if (typeof oldestSessionId !== "string") {
      break
    }
    replayWorkerBypassLogSignatureBySessionId.delete(oldestSessionId)
  }
  debugWarn("sync.new", "Bypassed replay worker for large active runtime", {
    sessionId: args.sessionId,
    reason: args.reason,
    ...args.details,
  })
}

function shouldLatchReplayWorkerBypass(args: {
  reason: ThreadSyncReplayWorkerBypassReason
}): boolean {
  return (
    args.reason === "snapshot_timeline_too_large"
    || args.reason === "opencode_message_graph_too_large"
    || args.reason === "claude_raw_event_history_too_large"
    || args.reason === "runtime_chunk_budget_too_large"
    || args.reason === "estimated_current_state_bytes_too_large"
    || args.reason === "estimated_response_projection_bytes_too_large"
  )
}

function capturePatchedSnapshotOverlay(): {
  activeEditOperation?: AgentRuntimeActiveEditOperation | null
  sessionActionOutcome?: AgentRuntimeActionOutcome | null
} | null {
  const snapshot = useAgentRuntime.getState().snapshot
  if (!snapshot) {
    return null
  }

  const hasActiveEditOperation = snapshot.activeEditOperation !== null
  const hasSessionActionOutcome = snapshot.sessionActionOutcome !== null
  if (!hasActiveEditOperation && !hasSessionActionOutcome) {
    return null
  }

  return {
    activeEditOperation: hasActiveEditOperation ? snapshot.activeEditOperation : undefined,
    sessionActionOutcome: hasSessionActionOutcome ? snapshot.sessionActionOutcome : undefined,
  }
}

function reapplyPatchedSnapshotOverlay(overlay: ReturnType<typeof capturePatchedSnapshotOverlay>): void {
  if (!overlay) {
    return
  }

  patchActiveRuntimeSnapshot({
    activeEditOperation: overlay.activeEditOperation,
    sessionActionOutcome: overlay.sessionActionOutcome,
  })
}

function applyComputedSyncEventsToRuntime(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
  resume: ThreadSyncResumeState
  resolvePersistResume?: (args: {
    resume: ThreadSyncResumeState
    applyResultStatus: ThreadSyncRuntimeApplyResult["status"]
    maxEventId: number | null
  }) => Pick<ThreadSyncResumeState, "cursor" | "historyToken">
  connectionVersion: number
  computed: ComputedRawSyncEventsBatch
  expectedStateVersion: number
  nowMs: number
  onRecoverableError?: () => void
}): ThreadSyncRuntimeApplyResult | typeof COMPUTED_RUNTIME_STATE_VERSION_CONFLICT {
  if (args.activeThreadId !== args.sessionId) {
    return { status: "noop", maxEventId: null }
  }

  const hasCursorOnlyProgress =
    args.computed.appliedCount <= 0 &&
    args.computed.maxEventId !== null &&
    args.computed.maxEventId > args.resume.cursor

  if (!args.computed.didChange && !hasCursorOnlyProgress) {
    return {
      status: "noop",
      maxEventId: args.computed.maxEventId,
    }
  }

  if (hasCursorOnlyProgress && isNonAuthoritativeRuntimeMaterialization(args.runtime.materialization)) {
    return COMPUTED_RUNTIME_STATE_VERSION_CONFLICT
  }

  const patchedOverlay = args.computed.didChange ? capturePatchedSnapshotOverlay() : null

  if (args.computed.didChange) {
    const committed = useAgentRuntime.getState().commitComputedRawSyncEventsBatch({
      computed: args.computed,
      expectedStateVersion: args.expectedStateVersion,
    })

    if (!committed) {
      return COMPUTED_RUNTIME_STATE_VERSION_CONFLICT
    }
  }

  if (args.computed.appliedCount <= 0 && !hasCursorOnlyProgress) {
    return {
      status: "noop",
      maxEventId: args.computed.maxEventId,
    }
  }

  const resumeForPersistence = args.resolvePersistResume
    ? args.resolvePersistResume({
      resume: args.resume,
      applyResultStatus: "applied",
      maxEventId: args.computed.maxEventId,
    })
    : args.resume

  finalizeSuccessfulRuntimeApply({
    activeThreadId: args.activeThreadId,
    sessionId: args.sessionId,
    runtime: args.runtime,
    connectionVersion: args.connectionVersion,
    resume: resumeForPersistence,
    persistAsPartial: args.resume.isCatchingUp,
    nowMs: args.nowMs,
  })
  if (patchedOverlay) {
    reapplyPatchedSnapshotOverlay(patchedOverlay)
  }
  args.runtime.lastApplyError = null
  return {
    status: "applied",
    maxEventId: args.computed.maxEventId,
  }
}

export function applyBaselinePageToRuntime(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
  resume: ThreadSyncResumeState
  connectionVersion: number
  frame: {
    agentType: AgentType
    events: Array<{
      baselineSeq: number
      type: string
      payload: unknown
      timestamp: number
    }>
  }
  nowMs: number
  onRecoverableError?: () => void
}): ThreadSyncRuntimeApplyResult {
  return runThreadSyncRuntimeApplyTransaction({
    activeThreadId: args.activeThreadId,
    sessionId: args.sessionId,
    runtime: args.runtime,
    connectionVersion: args.connectionVersion,
    resumeForPersistence: args.resume,
    sourceCursor: args.resume.cursor,
    persistAsPartial: true,
    nowMs: args.nowMs,
    failureReason: "Failed to apply baseline events",
    onRecoverableError: args.onRecoverableError,
    performStoreApply: () => {
      const result = useAgentRuntime.getState().applyBaselineEvents({
        agentType: args.frame.agentType,
        events: args.frame.events.map((event) => ({
          baselineSeq: event.baselineSeq,
          eventType: event.type,
          payload: event.payload,
          timestamp: event.timestamp,
        })),
      })

      return {
        status: result.status,
        maxEventId: null,
      }
    },
  })
}

export function applySyncEventsToRuntime(args: {
  activeThreadId: string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
  resume: ThreadSyncResumeState
  resolvePersistResume?: (args: {
    resume: ThreadSyncResumeState
    applyResultStatus: ThreadSyncRuntimeApplyResult["status"]
    maxEventId: number | null
  }) => Pick<ThreadSyncResumeState, "cursor" | "historyToken">
  connectionVersion: number
  events: Array<{
    id: number
    type: string
    payload: unknown
    timestamp: number
    childSessionId?: string
  }>
  nowMs: number
  onRecoverableError?: () => void
}): ThreadSyncRuntimeApplyResult {
  if (args.activeThreadId !== args.sessionId || args.events.length === 0) {
    return { status: "noop", maxEventId: null }
  }

  const resumeForPersistence = args.resolvePersistResume
    ? args.resolvePersistResume({
      resume: args.resume,
      applyResultStatus: "applied",
      maxEventId: args.events.at(-1)?.id ?? null,
    })
    : args.resume

  return runThreadSyncRuntimeApplyTransaction({
    activeThreadId: args.activeThreadId,
    sessionId: args.sessionId,
    runtime: args.runtime,
    connectionVersion: args.connectionVersion,
    resumeForPersistence,
    sourceCursor: args.resume.cursor,
    persistAsPartial: args.resume.isCatchingUp,
    nowMs: args.nowMs,
    failureReason: "Failed to apply sync events",
    onRecoverableError: args.onRecoverableError,
    performStoreApply: () => {
      const result = useAgentRuntime.getState().applyRawSyncEventsBatch(
        args.events.map((event) => ({
          eventType: event.type,
          payload: event.payload,
          eventId: event.id,
          childSessionId: event.childSessionId,
          timestamp: event.timestamp,
        })),
      )

      return {
        status: result.appliedCount > 0 ? "applied" : "noop",
        maxEventId: result.maxEventId,
      }
    },
  })
}

export async function applySyncEventsToRuntimeInChunks(args: {
  activeThreadId: string | null
  getCurrentActiveThreadId?: () => string | null
  sessionId: string
  runtime: ThreadSyncRuntimeState
  resume: ThreadSyncResumeState
  resolvePersistResume?: (args: {
    resume: ThreadSyncResumeState
    applyResultStatus: ThreadSyncRuntimeApplyResult["status"]
    maxEventId: number | null
  }) => Pick<ThreadSyncResumeState, "cursor" | "historyToken">
  connectionVersion: number
  events: Array<{
    id: number
    type: string
    payload: unknown
    timestamp: number
    childSessionId?: string
  }>
  nowMs: number
  chunkEventCount?: number
  preferOffMainThread?: boolean
  shouldContinue?: () => boolean
  onRecoverableError?: () => void
  onChunkApplied?: (args: {
    chunkIndex: number
    totalChunks: number
    chunkEvents: Array<{
      id: number
      type: string
      payload: unknown
      timestamp: number
      childSessionId?: string
    }>
    applyResult: ThreadSyncRuntimeApplyResult
  }) => void
}): Promise<ThreadSyncChunkedRuntimeApplyResult> {
  const getCurrentActiveThreadId = args.getCurrentActiveThreadId ?? (() => args.activeThreadId)

  if (getCurrentActiveThreadId() !== args.sessionId || args.events.length === 0) {
    return { status: "noop", maxEventId: null, completedAllChunks: true }
  }

  const chunkEventCount =
    typeof args.chunkEventCount === "number" && Number.isFinite(args.chunkEventCount) && args.chunkEventCount > 0
      ? Math.floor(args.chunkEventCount)
      : THREAD_SYNC_BULK_APPLY_CHUNK_EVENT_COUNT

  if (args.events.length <= chunkEventCount) {
    return {
      ...applySyncEventsToRuntime({
        ...args,
        activeThreadId: getCurrentActiveThreadId(),
      }),
      completedAllChunks: true,
    }
  }

  const totalChunks = Math.ceil(args.events.length / chunkEventCount)
  let finalResult: ThreadSyncRuntimeApplyResult = { status: "noop", maxEventId: null }
  let rollingResume = args.resume
  let replayWorkerBypassIsLatched = false
  let replayWorkerSessionId: string | null = null
  let replayWorkerSessionStateVersion: number | null = null
  let replayWorkerSessionEstimatedProjectionBytes: number | null = null
  let replayWorkerSessionProjectionSizeEstimateWasTruncated = false
  let replayWorkerSessionFailed = false

  function disposeReplayWorkerSession(): void {
    disposeThreadSyncReplayWorkerSession({ replaySessionId: replayWorkerSessionId })
    replayWorkerSessionId = null
    replayWorkerSessionStateVersion = null
    replayWorkerSessionEstimatedProjectionBytes = null
    replayWorkerSessionProjectionSizeEstimateWasTruncated = false
  }

  try {
    for (let startIndex = 0; startIndex < args.events.length; startIndex += chunkEventCount) {
      if (args.shouldContinue && !args.shouldContinue()) {
        disposeReplayWorkerSession()
        return {
          ...finalResult,
          completedAllChunks: false,
        }
      }

      const chunkIndex = Math.floor(startIndex / chunkEventCount)
      const chunkEvents = args.events.slice(startIndex, startIndex + chunkEventCount)
      let applyResult: ThreadSyncRuntimeApplyResult

      if (args.preferOffMainThread !== false && !replayWorkerBypassIsLatched && !replayWorkerSessionFailed) {
        const replayWorkerEvents: ThreadSyncReplayWorkerEvent[] = chunkEvents.map((event) => ({
          eventType: event.type,
          payload: event.payload,
          eventId: event.id,
          childSessionId: event.childSessionId,
          timestamp: event.timestamp,
        }))
        const { currentState, stateVersion } = buildComputationStateFromActiveStore()
        if (replayWorkerSessionId !== null && replayWorkerSessionStateVersion !== stateVersion) {
          disposeReplayWorkerSession()
        }
        const replayWorkerRequestShape = replayWorkerSessionId === null ? "start" : "continue"
        const replayWorkerEligibility = evaluateThreadSyncReplayWorkerEligibility({
          currentState,
          chunkEventCount: chunkEvents.length,
          chunkEvents: replayWorkerEvents,
          requestShape: replayWorkerRequestShape,
          continuedProjectionBytes: replayWorkerSessionEstimatedProjectionBytes,
          continuedProjectionBytesWasTruncated: replayWorkerSessionProjectionSizeEstimateWasTruncated,
          totalChunkCount: totalChunks,
        })

        if (!replayWorkerEligibility.allowed) {
          if (chunkIndex === 0) {
            maybeLogReplayWorkerBypass({
              sessionId: args.sessionId,
              reason: replayWorkerEligibility.reason,
              details: { ...replayWorkerEligibility.summary },
            })
          }
          if (shouldLatchReplayWorkerBypass({ reason: replayWorkerEligibility.reason })) {
            replayWorkerBypassIsLatched = true
          }
          if (replayWorkerSessionId !== null) {
            disposeReplayWorkerSession()
          }

          applyResult = applySyncEventsToRuntime({
            activeThreadId: getCurrentActiveThreadId(),
            sessionId: args.sessionId,
            runtime: args.runtime,
            resume: rollingResume,
            resolvePersistResume: args.resolvePersistResume,
            connectionVersion: args.connectionVersion,
            events: chunkEvents,
            nowMs: args.nowMs,
            onRecoverableError: args.onRecoverableError,
          })
        } else {
          const expectedStateVersion: number = stateVersion
          const replayComputationStartedAtMs = nowForReplayComputationMeasurement()
          const replayWorkerComputation = await computeRawSyncEventsBatchOffMainThread({
            currentState,
            events: replayWorkerEvents,
            replaySessionId: replayWorkerSessionId,
          })
          replayWorkerSessionFailed = replayWorkerComputation.replaySessionId === null
          replayWorkerSessionId = replayWorkerComputation.replaySessionId
          replayWorkerSessionEstimatedProjectionBytes = replayWorkerComputation.estimatedProjectionBytes
          replayWorkerSessionProjectionSizeEstimateWasTruncated =
            replayWorkerComputation.projectionSizeEstimateWasTruncated
          const computed = replayWorkerComputation.computed
          const replayComputationDurationMs = Math.round(
            nowForReplayComputationMeasurement() - replayComputationStartedAtMs,
          )

          if (
            chunkIndex === 0 &&
            replayComputationDurationMs >= THREAD_SYNC_REPLAY_COMPUTATION_SLOW_LOG_THRESHOLD_MS
          ) {
            debugInfo("sync.new", "Replay batch computation was slow", {
              sessionId: args.sessionId,
              durationMs: replayComputationDurationMs,
              totalChunks,
              ...replayWorkerEligibility.summary,
            })
          }

          if (args.shouldContinue && !args.shouldContinue()) {
            disposeReplayWorkerSession()
            return {
              ...finalResult,
              completedAllChunks: false,
            }
          }
          const computedApplyResult = applyComputedSyncEventsToRuntime({
            activeThreadId: getCurrentActiveThreadId(),
            sessionId: args.sessionId,
            runtime: args.runtime,
            resume: rollingResume,
            resolvePersistResume: args.resolvePersistResume,
            connectionVersion: args.connectionVersion,
            computed,
            expectedStateVersion,
            nowMs: args.nowMs,
            onRecoverableError: args.onRecoverableError,
          })
          if (computedApplyResult === COMPUTED_RUNTIME_STATE_VERSION_CONFLICT) {
            disposeReplayWorkerSession()
            if (args.shouldContinue && !args.shouldContinue()) {
              return {
                ...finalResult,
                completedAllChunks: false,
              }
            }

            applyResult = applySyncEventsToRuntime({
              activeThreadId: getCurrentActiveThreadId(),
              sessionId: args.sessionId,
              runtime: args.runtime,
              resume: rollingResume,
              resolvePersistResume: args.resolvePersistResume,
              connectionVersion: args.connectionVersion,
              events: chunkEvents,
              nowMs: args.nowMs,
              onRecoverableError: args.onRecoverableError,
            })
          } else {
            replayWorkerSessionStateVersion = useAgentRuntime.getState().stateVersion
            applyResult = computedApplyResult
          }
        }
      } else {
        applyResult = applySyncEventsToRuntime({
          activeThreadId: getCurrentActiveThreadId(),
          sessionId: args.sessionId,
          runtime: args.runtime,
          resume: rollingResume,
          resolvePersistResume: args.resolvePersistResume,
          connectionVersion: args.connectionVersion,
          events: chunkEvents,
          nowMs: args.nowMs,
          onRecoverableError: args.onRecoverableError,
        })
      }

      finalResult = {
        status:
          applyResult.status === "error"
            ? "error"
            : (finalResult.status === "applied" || applyResult.status === "applied")
              ? "applied"
              : "noop",
        maxEventId: applyResult.maxEventId ?? finalResult.maxEventId,
      }

      if (getCurrentActiveThreadId() !== args.sessionId) {
        disposeReplayWorkerSession()
        return {
          ...finalResult,
          completedAllChunks: false,
        }
      }

      args.onChunkApplied?.({
        chunkIndex,
        totalChunks,
        chunkEvents,
        applyResult,
      })

      const lastChunkEventId = chunkEvents.at(-1)?.id ?? null
      const persistedResume = args.resolvePersistResume
        ? args.resolvePersistResume({
          resume: rollingResume,
          applyResultStatus: applyResult.status,
          maxEventId: applyResult.maxEventId,
        })
        : {
          cursor: rollingResume.cursor,
          historyToken: rollingResume.historyToken,
        }

      rollingResume = {
        ...rollingResume,
        ...persistedResume,
        cursor:
          applyResult.status === "error" || lastChunkEventId === null
            ? rollingResume.cursor
            : Math.max(rollingResume.cursor, lastChunkEventId),
        lastPageCount: chunkEvents.length,
        lastPageBytes: 0,
      }

      if (applyResult.status === "error") {
        disposeReplayWorkerSession()
        return {
          ...applyResult,
          completedAllChunks: false,
        }
      }

      if (chunkIndex < totalChunks - 1) {
        await yieldAfterBulkApplySlice()
      }
    }
    return {
      ...finalResult,
      completedAllChunks: true,
    }
  } finally {
    disposeReplayWorkerSession()
  }
}
